import {
  duckDbIdentifier,
  duckDbStringLiteral,
} from '@cotera/client/app/etc/duckdb';
import {
  dataGridColumnTypeFromSqlType,
  isComparisonFilterValue,
  isStructuredFilterValue,
} from '@cotera/client/app/components/ui/data-grid';
import type {
  DataGridComparisonFilterValue,
  DataGridFilter,
  DataGridFilterComparison,
  DataGridFilterScalar,
  DataGridSort,
} from '@cotera/client/app/components/ui/data-grid';
import type { DatasetArtifactMetadataOutput } from '@cotera/client/app/etc/api/typed-client';
import { isInternalDatasetColumn } from '@cotera/sdk/core';
import { DATASET_PREVIEW_ORDER_COLUMN } from './operations';
import { DATASET_PREVIEW_SOURCE_ALIAS } from './source-sql';

export const sortSql = (
  sorts: DataGridSort[],
  columns: DatasetArtifactMetadataOutput['schema']['columns'],
  /**
   * Whether the source still carries {@link DATASET_PREVIEW_ORDER_COLUMN}, and
   * so can be given a stable default order.
   */
  ordered = false
): string => {
  const activeSorts = sorts.filter((sort) =>
    columns.some((column) => column.name === sort.columnId)
  );
  if (activeSorts.length === 0) {
    // A paged read with no ORDER BY is undefined: DuckDB may hand back scan
    // order today, but nothing holds it steady across pages, least of all
    // after the operation replay has issued UPDATEs and INSERTs against the
    // table. Without this, rows can repeat or disappear between pages, and
    // the fractional ordering that positions an inserted row does nothing at
    // all because nothing reads it.
    return ordered
      ? ` ORDER BY ${duckDbIdentifier(DATASET_PREVIEW_ORDER_COLUMN)}`
      : '';
  }
  return ` ORDER BY ${activeSorts
    .map(
      (sort) =>
        `${duckDbIdentifier(sort.columnId)} ${sort.direction.toUpperCase()}`
    )
    .join(', ')}`;
};

const isTemporalColumn = (
  column: DatasetArtifactMetadataOutput['schema']['columns'][number]
): boolean => {
  const type = dataGridColumnTypeFromSqlType(column.type);
  return type === 'date' || type === 'timestamp';
};

const COMPARISON_OPERATORS: Record<DataGridFilterComparison, string | null> = {
  equals: '=',
  notEquals: '<>',
  greaterThan: '>',
  greaterThanOrEqual: '>=',
  lessThan: '<',
  lessThanOrEqual: '<=',
  // Handled ahead of the operator table, having no right-hand operand or no
  // single operator.
  contains: null,
  isNull: null,
  isNotNull: null,
};

/**
 * The two sides of a comparison, read as the column's own type: numbers stay
 * numbers, dates become timestamps, and everything else compares as text.
 * Null when the operand cannot be read that way — a half-typed number, say.
 */
const comparisonOperandsSql = (
  identifier: string,
  value: DataGridFilterScalar,
  column: DatasetArtifactMetadataOutput['schema']['columns'][number]
): { subject: string; literal: string } | null => {
  if (isTemporalColumn(column)) {
    return {
      subject: `CAST(${identifier} AS TIMESTAMP)`,
      literal: `CAST(${duckDbStringLiteral(String(value))} AS TIMESTAMP)`,
    };
  }
  const type = dataGridColumnTypeFromSqlType(column.type);
  if (type === 'boolean') {
    return {
      subject: identifier,
      literal: value === true || value === 'true' ? 'TRUE' : 'FALSE',
    };
  }
  if (type === 'number') {
    const numeric = Number(value);
    return Number.isFinite(numeric)
      ? { subject: identifier, literal: String(numeric) }
      : null;
  }
  return {
    subject: `${identifier}::VARCHAR`,
    literal: duckDbStringLiteral(String(value)),
  };
};

const comparisonClauseSql = (
  identifier: string,
  filter: DataGridComparisonFilterValue,
  column: DatasetArtifactMetadataOutput['schema']['columns'][number]
): string | null => {
  if (filter.comparison === 'isNull') {
    return `${identifier} IS NULL`;
  }
  if (filter.comparison === 'isNotNull') {
    return `${identifier} IS NOT NULL`;
  }
  if (filter.comparison === 'contains') {
    return `${identifier}::VARCHAR ILIKE ${duckDbStringLiteral(
      `%${String(filter.value)}%`
    )}`;
  }
  const operator = COMPARISON_OPERATORS[filter.comparison];
  const operands = comparisonOperandsSql(identifier, filter.value, column);
  return operator === null || operands === null
    ? null
    : `${operands.subject} ${operator} ${operands.literal}`;
};

/**
 * Translates one grid filter into DuckDB. Scalar values keep the existing
 * substring behaviour; comparison values come from the header's filter form
 * and structured values from stat marks, translated as typed predicates.
 */
const filterClauseSql = (
  filter: DataGridFilter,
  column: DatasetArtifactMetadataOutput['schema']['columns'][number]
): string | null => {
  const identifier = duckDbIdentifier(filter.columnId);
  const value = filter.value;

  if (isComparisonFilterValue(value)) {
    return comparisonClauseSql(identifier, value, column);
  }

  if (isStructuredFilterValue(value)) {
    if (value.kind === 'in') {
      const clauses: string[] = [];
      const nonNull = value.values.filter((entry) => entry !== null);
      if (nonNull.length > 0) {
        clauses.push(
          `${identifier}::VARCHAR IN (${nonNull
            .map((entry) => duckDbStringLiteral(String(entry)))
            .join(', ')})`
        );
      }
      if (value.values.some((entry) => entry === null)) {
        clauses.push(`${identifier} IS NULL`);
      }
      return clauses.length === 0 ? null : `(${clauses.join(' OR ')})`;
    }

    const upperOperator = value.inclusiveMax === true ? '<=' : '<';
    if (isTemporalColumn(column)) {
      return `(CAST(${identifier} AS TIMESTAMP) >= CAST(${duckDbStringLiteral(
        String(value.min)
      )} AS TIMESTAMP) AND CAST(${identifier} AS TIMESTAMP) ${upperOperator} CAST(${duckDbStringLiteral(
        String(value.max)
      )} AS TIMESTAMP))`;
    }
    const min = Number(value.min);
    const max = Number(value.max);
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return null;
    }
    return `(${identifier} >= ${min} AND ${identifier} ${upperOperator} ${max})`;
  }

  return `${identifier}::VARCHAR ILIKE ${duckDbStringLiteral(
    `%${String(value)}%`
  )}`;
};

const filterClauses = (
  filters: DataGridFilter[],
  columns: DatasetArtifactMetadataOutput['schema']['columns']
): string[] =>
  filters.flatMap((filter) => {
    const column = columns.find(
      (candidate) => candidate.name === filter.columnId
    );
    if (column === undefined || filter.value === null) {
      return [];
    }
    const clause = filterClauseSql(filter, column);
    return clause === null ? [] : [clause];
  });

const whereFrom = (clauses: string[]): string =>
  clauses.length === 0 ? '' : ` WHERE ${clauses.join(' AND ')}`;

export const filterSql = (
  filters: DataGridFilter[],
  columns: DatasetArtifactMetadataOutput['schema']['columns']
): string => whereFrom(filterClauses(filters, columns));

/**
 * The character that turns off `%` and `_` in a LIKE pattern.
 *
 * A search box takes whatever was typed, and `50%` is three characters a
 * reader expects to find literally — left alone it is a pattern meaning
 * "starts with 50", which silently returns rows that do not contain the term.
 * Backslash rather than anything cleverer because DuckDB's single-quoted
 * literals are standard SQL: nothing inside them is escaped, so the character
 * survives the trip to the parser as itself.
 */
const LIKE_ESCAPE = '\\';

const escapeLikePattern = (value: string): string =>
  value.replace(/[\\%_]/g, (character) => `${LIKE_ESCAPE}${character}`);

/**
 * Every value in the row as one string, with the column names left out.
 *
 * The fallback for a statement whose projection we do not know: it names no
 * column, so it cannot name one the source dropped. `to_json` would carry the
 * keys along with the values and make a search for `name` match every row that
 * has such a column, so the values are pulled back out of it and joined on a
 * separator no keyboard produces.
 *
 * Slower than the per-column ILIKEs — it serializes each row — which is why it
 * is the fallback rather than the path. It only runs where the reader has
 * already narrowed the source with a statement of their own.
 */
const rowValuesSql = (): string =>
  `array_to_string(json_extract_string(to_json(${duckDbIdentifier(
    DATASET_PREVIEW_SOURCE_ALIAS
  )}), '$.*'), chr(31))`;

/**
 * The characters a column of this type can possibly render as text, or null
 * where we do not know and must not guess.
 *
 * The point is to skip columns a term cannot possibly be found in: searching
 * `acme` over a table of six numeric columns and four text ones should read
 * four columns, not ten. Only a *necessary* condition is being tested — every
 * character of the term has to be one this type can produce — so a column is
 * only ever dropped when a match is impossible, never when it is merely
 * unlikely.
 *
 * The alphabets come from what DuckDB actually prints, which is not what one
 * would guess: a DOUBLE renders infinity as `inf` and not-a-number as `nan`,
 * and exponents as `1e+300`, so its alphabet carries five letters. A DATE
 * before year 1 renders `0045-03-15 (BC)`, so the temporal alphabet carries
 * brackets, a space and two more letters.
 *
 * Null for an untyped column and for everything not named, which is the safe
 * answer and the common one:
 * text, categories, blobs, structs, lists, JSON — and intervals, which matter
 * here because `'INTERVAL'.includes('INT')` makes the grid's own type mapper
 * call them numbers. An interval prints `3 days`; skipping one on a search for
 * `days` would drop rows that do contain the term, which is the only failure
 * this whole idea can have.
 */
const searchAlphabet = (sqlType: string | null): string | null => {
  // A column that never said what it holds is one we cannot rule out.
  if (sqlType === null) {
    return null;
  }
  // `DECIMAL(10,3)` and `TIMESTAMP WITH TIME ZONE` both matter, so the
  // parameters go and the words stay.
  const name = sqlType.split('(')[0]?.trim().toUpperCase() ?? '';
  if (NUMERIC_TYPE_NAMES.has(name)) {
    // Digits and a decimal point, a sign, an exponent's `e`, and the letters
    // of `inf` and `nan`.
    return '0123456789.+-einfa';
  }
  if (TEMPORAL_TYPE_NAMES.has(name)) {
    // `2026-01-02 03:04:05.123`, a `-05`/`+02` zone, and `(BC)`.
    return '0123456789-:.+ ()bc';
  }
  return null;
};

const NUMERIC_TYPE_NAMES: ReadonlySet<string> = new Set([
  'TINYINT',
  'SMALLINT',
  'INTEGER',
  'BIGINT',
  'HUGEINT',
  'UTINYINT',
  'USMALLINT',
  'UINTEGER',
  'UBIGINT',
  'UHUGEINT',
  'FLOAT',
  'DOUBLE',
  'REAL',
  'DECIMAL',
  'NUMERIC',
]);

const TEMPORAL_TYPE_NAMES: ReadonlySet<string> = new Set([
  'DATE',
  'TIME',
  'TIMETZ',
  'TIME WITH TIME ZONE',
  'TIMESTAMP',
  'TIMESTAMPTZ',
  'TIMESTAMP WITH TIME ZONE',
  'DATETIME',
]);

const BOOLEAN_TYPE_NAMES: ReadonlySet<string> = new Set(['BOOLEAN', 'BOOL']);

/**
 * Whether this column could contain the term at all.
 *
 * The cheapest optimisation available to a search that reads every column: the
 * predicate for a column that cannot match is work whose answer is known before
 * the scan starts. Measured on a 200k-row table of twelve columns, dropping the
 * eight that a word cannot appear in took the page query from 21ms to 5ms and
 * the row count — which cannot stop early the way a paged read can, and so
 * dominates — from 134ms to 54ms.
 */
const columnCanContain = (
  term: string,
  column: DatasetArtifactMetadataOutput['schema']['columns'][number]
): boolean => {
  const lowered = term.toLowerCase();
  const name = column.type?.split('(')[0]?.trim().toUpperCase() ?? '';
  if (BOOLEAN_TYPE_NAMES.has(name)) {
    // Exactly two values, so the question is exact rather than alphabetic:
    // `fal` can be found in a boolean column and `flase` cannot.
    return 'true'.includes(lowered) || 'false'.includes(lowered);
  }
  const alphabet = searchAlphabet(column.type);
  return (
    alphabet === null ||
    [...lowered].every((character) => alphabet.includes(character))
  );
};

/**
 * One box, every column: an OR of substring matches over the whole row.
 *
 * Cast to text rather than restricted to the text columns, because what is
 * being searched for does not know how it was stored — `2026` is a year in a
 * timestamp column, a value in a numeric one and a fragment of an id in a
 * third, and a reader typing it into one box means all three. That is also the
 * bargain this search makes: it is a substring scan, not an index, so it is
 * exactly as fast as DuckDB can read the column and no faster.
 *
 * Internal bookkeeping columns are left out. They are ours rather than the
 * user's data, and a hit on a row id nobody can see puts a row on screen with
 * no visible reason for being there.
 *
 * A statement that narrowed the projection gets {@link rowValuesSql} instead,
 * which asks the same question without naming a column.
 *
 * Null when there is nothing to search for, which is the caller's signal to
 * add no clause at all rather than one that is always true.
 */
export const searchClauseSql = (
  search: string,
  columns: DatasetArtifactMetadataOutput['schema']['columns'],
  /**
   * Whether the source is known to project `columns`. False once an ad-hoc
   * statement has chosen its own shape, which is when naming them is what
   * breaks the query.
   */
  projectsKnownColumns = true
): string | null => {
  const term = search.trim();
  if (term === '') {
    return null;
  }
  const pattern = duckDbStringLiteral(`%${escapeLikePattern(term)}%`);
  const escape = duckDbStringLiteral(LIKE_ESCAPE);
  if (!projectsKnownColumns) {
    return `(${rowValuesSql()} ILIKE ${pattern} ESCAPE ${escape})`;
  }
  const searchable = columns.filter(
    (column) =>
      !isInternalDatasetColumn(column.name) && columnCanContain(term, column)
  );
  if (searchable.length === 0) {
    // No column this term could be in, so it is in none of them. `FALSE`
    // rather than no clause at all: dropping the predicate would answer a
    // search that matches nothing with every row in the dataset.
    return '(FALSE)';
  }
  return `(${searchable
    .map(
      (column) =>
        `${duckDbIdentifier(
          column.name
        )}::VARCHAR ILIKE ${pattern} ESCAPE ${escape}`
    )
    .join(' OR ')})`;
};

/**
 * Everything narrowing the preview, as one WHERE.
 *
 * The header filters and the search box are ANDed rather than merged: they are
 * different questions — "only rows where this column is X" and "rows mentioning
 * X anywhere" — and a reader who has set both means both. The search is
 * parenthesised on its way in, so its ORs cannot swallow a filter beside it.
 */
export const previewWhereSql = (input: {
  filters: DataGridFilter[];
  search: string;
  columns: DatasetArtifactMetadataOutput['schema']['columns'];
  /** See {@link searchClauseSql}; only the search reads it. */
  projectsKnownColumns?: boolean;
}): string => {
  const search = searchClauseSql(
    input.search,
    input.columns,
    input.projectsKnownColumns
  );
  return whereFrom([
    ...filterClauses(input.filters, input.columns),
    ...(search === null ? [] : [search]),
  ]);
};
